import { ExclamationCircleOutlined } from "@ant-design/icons";
import confirm from "antd/lib/modal/confirm";
import { useBulkInsertForumTablesMutation, useBulkInsertTablesMutation } from "api/mutations/tables";
import { Button, Col, Drawer, Form, Input, Row, Select } from "components/styleguide";
import { AdminTypes } from "enums/common";
import ErrorTypes from "error-handling/errorTypes";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { validateIntegerNumberRange, validateWithRegex } from "utils/validatorUtils";

import { getTableTypeOptions } from "../helpers/tableHelper";

import styles from "./styles.module.less";

export default function BulkAddTables({ ui, visible, form, onClose, onFinish, facility, tablesLoading }) {
  const { t } = useTranslation();
  const params = useParams();
  const [venueId, buildingId, forumId] = [params.venueId, params.buildingId, params.id].map((id) => parseInt(id));

  const insertBulkForumTablesMutation = useBulkInsertForumTablesMutation();
  const insertBulkTablesMutation = useBulkInsertTablesMutation();
  const isSysAdminUi = ui === AdminTypes.SystemAdmin;
  const tableTypes = useMemo(() => getTableTypeOptions(), []);

  const buildPayload = (values, forumId, vanueId, buildingId, facilityId, forceGenerate) => {
    const settings = {
      FirstTableNumber: values.tableNumber,
      TablePrefix: values.tablePrefix,
      MaxNumberOfDigits: values.maxNumberOfDigits,
      NumberOfTables: values.numberOfTables,
      NumberOfSeats: values.numberOfSeats,
      TypeOfTable: values.tableType,
    };

    if (isSysAdminUi) {
      return {
        venueId: vanueId,
        buildingId: buildingId,
        facilityId: facilityId,
        tableGenerationSettingsModel: settings,
        forceGenerate,
      };
    } else {
      return {
        forumId: forumId,
        forumBuildingId: buildingId,
        forumFacilityId: facilityId,
        tableGenerationSettingsModel: settings,
        forceGenerate,
      };
    }
  };

  const handleBulkGenerate = (values, forceGenerate) => {
    const payload = buildPayload(values, forumId, venueId, buildingId, facility.id, forceGenerate);

    if (isSysAdminUi) {
      insertBulkTablesMutation.mutate(payload, {
        onSuccess: handleSuccess,
        onError: handleError,
      });
    } else {
      insertBulkForumTablesMutation.mutate(payload, {
        onSuccess: handleSuccess,
        onError: handleError,
      });
    }
  };

  const onGenerate = () => {
    form.validateFields().then(() => {
      const values = form.getFieldsValue();
      handleBulkGenerate(values, false);
    });
  };

  const onForceGenerate = () => {
    form.validateFields().then(() => {
      const values = form.getFieldsValue();
      handleBulkGenerate(values, true);
    });
  };

  const handleSuccess = (response) => {
    if (response.data.hasConflicts) {
      toast.warning(t("bulkAddTables.conflicts"), { autoClose: 30000 });
      toast.warning(t("bulkAddTables.proceed"), { autoClose: 30000 });

      showConfirmModal(response.data);
    } else {
      toast.success(t("bulkAddTables.success", { count: response.data.createdCount }), { autoClose: 15000 });
      onFinish?.();
    }
  };

  const handleError = (error) => {
    const exception = error.response;
    if (ErrorTypes.isOfType(exception, ErrorTypes.InvalidModelState)) {
      const meta = exception.data?.meta;

      if (meta?.ModelFields) {
        Object.entries(meta.ModelFields).forEach(([, v]) => {
          toast.error(`${v}`, { autoClose: 30000 });
        });
      }
    } else if (ErrorTypes.isOfType(exception, ErrorTypes.ObjectNotFound)) {
      const meta = exception.data?.meta;

      toast.error(`${meta.ObjectType} not found.`, { autoClose: 30000 });
    } else {
      toast.error(t("bulkAddTables.unexpectedError"), { autoClose: 30000 });
    }
  };

  const showConfirmModal = (responseData) => {
    const visibleConflictTableNumbers = responseData.conflictNumbers.slice(0, 10); //only take first 10
    const hiddenTableConflictsCount = responseData.conflictNumbers.length - visibleConflictTableNumbers.length;

    confirm({
      title: t("confirmation"),
      icon: <ExclamationCircleOutlined />,
      okText: t("Proceed Anyway"),
      okType: "danger",
      confirmLoading: insertBulkTablesMutation.isLoading || insertBulkForumTablesMutation.isLoading,
      cancelText: t("Cancel"),
      onOk: () => {
        onForceGenerate();
      },
      content: (
        <>
          <p>{t("bulkAddTables.confirmText")}</p>

          <Form.Item style={{ marginTop: 12 }}>
            <p style={{ margin: 0 }}>
              {visibleConflictTableNumbers.map((n) => `${n}`).join(", ")}

              {hiddenTableConflictsCount > 0 && <>{t("bulkAddTables.andMore", { count: hiddenTableConflictsCount })}</>}
            </p>
          </Form.Item>
        </>
      ),
    });
  };

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      title={"Bulk Add Tables"}
      placement="right"
      contentWrapperStyle={{ minWidth: "50%" }}
      destroyOnClose
    >
      <Form
        id="bulkAddTablesForm"
        className={styles.form}
        labelWrap
        layout="vertical"
        name="new-tables"
        labelCol={{ justify: "left", span: 24 }}
        wrapperCol={{ justify: "right" }}
        onFinish={onGenerate}
        form={form}
      >
        <div className={styles.formLayout}>
          <div>
            <Form.Item
              label={t("First Table Number")}
              name="tableNumber"
              rules={[
                {
                  required: true,
                  message: t("errors.required", { prop: t("First Table Number") }),
                },
                {
                  validator: validateIntegerNumberRange(t, 1, 1000),
                },
              ]}
            >
              <Input type="number" />
            </Form.Item>
            <Form.Item
              label={t("Table Prefix")}
              name="tablePrefix"
              rules={[
                {
                  required: false,
                },
                {
                  max: 8,
                  message: t("'Table Prefix' field has a maximum of 8 characters"),
                },
                {
                  validator: validateWithRegex(/^[A-Za-z0-9_-]+$/),
                  message: t("'Table Prefix' field can only contain letters, numbers, '-' and '_'"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("Max Number of Digits")}
              name="maxNumberOfDigits"
              rules={[
                {
                  required: true,
                  message: t("errors.required", { prop: t("Max Number of Digits") }),
                },
                {
                  validator: validateIntegerNumberRange(t, 1, 8),
                },
              ]}
            >
              <Input type="number" />
            </Form.Item>
            <Form.Item
              label={t("No of Tables")}
              name="numberOfTables"
              rules={[
                {
                  required: true,
                  message: t("errors.required", { prop: t("No of Tables") }),
                },
                {
                  validator: validateIntegerNumberRange(t, 1, 200),
                },
              ]}
            >
              <Input type="number" />
            </Form.Item>
            <Form.Item
              label={t("No of Seats")}
              name="numberOfSeats"
              rules={[
                {
                  required: true,
                  message: t("errors.required", { prop: t("No of Seats") }),
                },
                {
                  validator: validateIntegerNumberRange(t, 1, 1000),
                },
              ]}
            >
              <Input type="number" />
            </Form.Item>
            <Form.Item
              label={t("Table Type")}
              name="tableType"
              rules={[
                {
                  required: true,
                  message: t("errors.required", { prop: t("Table Type") }),
                },
              ]}
            >
              <Select showSearch allowClear>
                {tableTypes}
              </Select>
            </Form.Item>
          </div>
          <div>
            <Row justify="space-between">
              <Col>
                <Button onClick={onClose}>{t("Cancel")}</Button>
              </Col>
              <Col>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={
                      tablesLoading || insertBulkTablesMutation.isLoading || insertBulkForumTablesMutation.isLoading
                    }
                  >
                    {t("Generate")}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>
      </Form>
    </Drawer>
  );
}

BulkAddTables.propTypes = {
  ui: PropTypes.oneOf(AdminTypes),
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onFinish: PropTypes.func,
  facility: PropTypes.object,
  tablesLoading: PropTypes.bool,
  form: PropTypes.any,
};
